/* ============================================================
   providerOrderView — TASK-253: pure view model for the provider
   order queue cards. Derives, from a ProviderOrder only:
   - `primary`: the single action-oriented next step (or what the
     provider is waiting on when the ball is not in their court);
   - `secondary`: extra affordances for the status (reject/offer,
     progress photos);
   - `cues`: scannable state chips (photo counts, customer
     confirmation, negotiation state);
   - `providerNextStatus`: the canonical status-advance map, shared
     by the card and the queue handler so it lives in one place.
   No fake data; every cue comes from real API fields. Light theme,
   Montserrat, iOS-18 minimal per DESIGN-SYSTEM.md.
   ============================================================ */

import type { ProviderOrder, OrderStatus } from '../types/provider';
import type { OrderTimelineInput } from './orderTimelineModel.ts';
import { buildDeliveryView, formatEtaDay, isPastCalendarDay } from './customerOrderView.ts';
import { isPickupDeliveryMethod } from './shippedTrackingView.ts';
import { buildDeliveryAddressStrip, type DeliveryAddressStrip } from './deliveryAddressStrip.ts';

export type ProviderPrimaryKind = 'accept' | 'startPrint' | 'attachResult' | 'ship' | 'complete';

export type CueTone = 'neutral' | 'accent' | 'success' | 'danger';

export interface ProviderOrderView {
  /** The one action-oriented next step; null when waiting on others. */
  primary: { kind: ProviderPrimaryKind; label: string } | null;
  /** Secondary affordances available in this status. */
  secondary: Array<'reject' | 'offer' | 'progressPhotos' | 'addTracking'>;
  /** What the provider is waiting on when there is no primary action. */
  waiting: { label: string; tone: 'neutral' | 'danger' | 'success' } | null;
  /** Scannable state chips for the card header. */
  cues: Array<{ label: string; tone: CueTone }>;
  /** G8-D1: delivery method/cost line for the queue card details. */
  delivery: { methodLabel: string; text: string };
  /** G8-D2: chosen self-pickup point when the customer picked one (address
   * is the order's shippingAddress; hours come from the point). */
  pickupPoint: { address: string; workingHours: string | null } | null;
  /** audit-3 A2: courier delivery address for Pickup-point-less orders (the point
   * address is not repeated here; null for pickup and address-less orders). */
  courierAddress: string | null;
  /** c3e7f6d0: carrier ПВЗ/postal service name — service name ONLY, the
   * point address never renders (Ozon ПВЗ / Почта / CDEK rows); null for
   * the two address kinds. */
  carrierService: string | null;
  /** c3e7f6d0: the shared address strip itself (queue rows + any future
   * surface render it via deliveryAddressStripText/Label/Value). */
  addressStrip: DeliveryAddressStrip | null;
  /** TASK-254: method-aware ETA row — pickup orders promise a collection
   * day («Выдача», same field the ShipForm date title and the overdue nudge
   * already call дата выдачи), courier/legacy keep «Доставка». Null when
   * the order has no promised date. */
  eta: { label: 'Доставка' | 'Выдача'; text: string } | null;
}

/** Canonical provider-driven advance; null when the provider cannot move the order. */
export function providerNextStatus(status: OrderStatus): OrderStatus | null {
  switch (status) {
    case 'Accepted': return 'Printing';
    case 'Printing': return 'Printed';
    case 'Printed': return null; // customer confirmation gates Shipped (TASK-234/235)
    case 'Shipped': return 'Completed';
    default: return null;        // Pending decision, Disputed (admin scope) or terminal
  }
}

/** audit5-B4: deadline chip stamp — same ru-RU shape as the customer's
 * browser-local cue (customerOrderView.formatTs), kept local so the pure
 * view model stays self-contained. */
function formatDeadlineTs(iso: string): string {
  return new Date(iso).toLocaleString('ru-RU', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Base card model (status-dependent part) — trivially unit-testable, no React dependency. */
function buildProviderOrderBase(order: ProviderOrder): Omit<ProviderOrderView, 'delivery' | 'pickupPoint' | 'courierAddress' | 'carrierService' | 'addressStrip' | 'eta'> {
  const confirmed = order.customerConfirmedAtUtc != null;
  // TASK-252: pickup handoff copy parity — самовывоз/ПВЗ orders are prepared
  // for collection, not dispatched (same predicate as TASK-248 customer copy;
  // legacy rows without a method snapshot keep the courier wording).
  const pickup = isPickupDeliveryMethod(order.deliveryMethod);
  const openOffer = order.openCounterOffer ?? null;
  const cues: ProviderOrderView['cues'] = [];

  if ((order.progressPhotoCount ?? 0) > 0) {
    cues.push({ label: `📷 ${order.progressPhotoCount} фото процесса`, tone: 'neutral' });
  }
  const resultCounted =
    order.status === 'Printed' || order.status === 'Shipped'
    || order.status === 'Completed' || order.status === 'Disputed';
  if (resultCounted && order.resultPhotoCount > 0) {
    cues.push({ label: `🖼 ${order.resultPhotoCount} фото результата`, tone: 'neutral' });
  }
  // TASK-258: the confirmation chip follows the handoff through
  // Shipped/Completed (history parity with the customer's ✓ cue) — the
  // provider sees *why* they may ship/complete even days later.
  if (
    (order.status === 'Printed' || order.status === 'Shipped' || order.status === 'Completed')
    && confirmed
  ) {
    cues.push({ label: '✓ Подтверждён клиентом', tone: 'success' });
  }
  if (openOffer) {
    cues.push(
      openOffer.isFromProvider
        ? { label: `Предложение отправлено: ${openOffer.proposedPriceRub}₽`, tone: 'neutral' }
        : { label: `Встречное предложение: ${openOffer.proposedPriceRub}₽`, tone: 'accent' },
    );
  }
  // audit5-B4 (1184af9f): the provider is the party waiting on the Printed-
  // unconfirmed order — cue the SAME deadline the customer already sees
  // (audit4-A2 basis) so pickup/ship work can be planned around it. Pure
  // view: rendered only while Printed-unconfirmed AND the server sent a
  // deadline (auto-confirm job enabled); no nudge channel (P2 bound).
  if (order.status === 'Printed' && !confirmed && order.confirmDeadlineUtc) {
    cues.push({
      label: `⏳ Авто-подтверждение до ${formatDeadlineTs(order.confirmDeadlineUtc)}`,
      tone: 'accent',
    });
  }

  switch (order.status) {
    case 'Pending':
      return {
        primary: { kind: 'accept', label: 'Принять заказ' },
        secondary: openOffer ? ['reject'] : ['reject', 'offer'],
        waiting: null,
        cues,
      };
    case 'Accepted':
      return {
        primary: { kind: 'startPrint', label: 'Начать печать' },
        secondary: ['progressPhotos'],
        waiting: null,
        cues,
      };
    case 'Printing':
      return {
        primary: { kind: 'attachResult', label: 'Прикрепить результат' },
        secondary: ['progressPhotos'],
        waiting: null,
        cues,
      };
    case 'Printed':
      return {
        // TASK-252: the primary verb follows the delivery method — pickup
        // orders are marked ready for collection at the point (stage
        // «Готов к выдаче»), courier orders are dispatched with tracking.
        primary: confirmed
          ? { kind: 'ship', label: pickup ? 'Готово к выдаче' : 'Отправить заказ' }
          : null,
        secondary: [],
        waiting: confirmed
          ? null
          : pickup
            ? { label: 'Ожидаем подтверждения клиента — выдача станет доступна после подтверждения результата.', tone: 'neutral' }
            : { label: 'Ожидаем подтверждения клиента — отправка станет доступна после подтверждения результата.', tone: 'neutral' },
        cues,
      };
    case 'Shipped':
      return {
        primary: { kind: 'complete', label: 'Завершить заказ' },
        // audit4-A3 (b10d4e95): the no-tracking Shipped email promised the
        // customer the number would appear once the provider adds it — the
        // one-shot patch affordance shows only while that promise is open.
        secondary: order.trackingNumber?.trim() ? [] : ['addTracking'],
        // TASK-256: the promised delivery day passed and the order is still
        // open — nudge the provider to close it out (primary stays 'complete').
        // TASK-252: the overdue verb follows the method (collection vs parcel).
        waiting: order.estimatedDeliveryUtc && isPastCalendarDay(order.estimatedDeliveryUtc)
          ? pickup
            ? { label: 'Ожидаемая дата выдачи прошла — завершите заказ, когда клиент его заберёт.', tone: 'neutral' }
            : { label: 'Ожидаемая дата доставки прошла — завершите заказ, когда клиент получит посылку.', tone: 'neutral' }
          : null,
        cues,
      };
    case 'Completed':
      return {
        primary: null,
        secondary: [],
        // TASK-258: dated terminal note — the queue history card closes the
        // loop instead of going silent once the provider completes.
        waiting: {
          label: order.completedAtUtc
            ? `Заказ завершён — ${new Date(order.completedAtUtc).toLocaleDateString('ru-RU')}`
            : 'Заказ завершён',
          tone: 'success',
        },
        cues,
      };
    case 'Cancelled':
      return { primary: null, secondary: [], waiting: null, cues };
    case 'Disputed': {
      // TASK-2416fdb8 (B5): echo the customer's own reason so the provider
      // sees what the dispute is about — trimmed, never fabricated (same
      // rules as the customer detail banner, TASK-667a55be).
      const reason = order.disputeReason?.trim();
      return {
        primary: null,
        secondary: [],
        waiting: {
          label: reason
            ? `Клиент открыл спор — заказ заморожен до решения поддержки. Причина: ${reason}`
            : 'Клиент открыл спор — заказ заморожен до решения поддержки.',
          tone: 'danger',
        },
        cues,
      };
    }
  }
}

/** G8-D1: pure card view model — base card + delivery method/cost line. */
export function buildProviderOrderView(order: ProviderOrder): ProviderOrderView {
  const delivery = buildDeliveryView({
    deliveryMethod: order.deliveryMethod,
    deliveryCostRub: order.deliveryCostRub,
    // Provider cards show the snapshot as-is; the estimate marker only
    // matters to the customer's own totals, so inclusion is not derived here.
    kind: null,
  });
  // c3e7f6d0: the shared address strip is the single source for all three
  // delivery shapes (самовывоз → point address, курьер продавца → client
  // address, ПВЗ/почта → service name only — Artem @b8810bf1). The legacy
  // pickupPoint/courierAddress fields below are now projections of it, so
  // the detail card and the queue row can never disagree.
  const addressStrip = buildDeliveryAddressStrip({
    deliveryMethod: order.deliveryMethod,
    shippingAddress: order.shippingAddress,
    pickupPointWorkingHours: order.pickupPointWorkingHours,
    deliveryCarrier: order.deliveryCarrier,
  });
  // G8-D2: surface the chosen point (address + hours) for Pickup orders;
  // courier orders and legacy pickup orders without a point get null.
  const pickupPoint = addressStrip?.kind === 'point'
    ? { address: addressStrip.address, workingHours: addressStrip.workingHours }
    : null;
  // TASK-254: method-aware ETA — pickup orders hand over for collection, so
  // the promised day is a «Выдача» day, not a «Доставка» one (TASK-252
  // already named the same field дата готовности к выдаче in the ShipForm
  // date title and the overdue nudge; the queue chip was the last courier-biased
  // label). Legacy rows without a method snapshot keep «Доставка».
  const eta = order.estimatedDeliveryUtc
    ? {
        label: isPickupDeliveryMethod(order.deliveryMethod) ? ('Выдача' as const) : ('Доставка' as const),
        text: formatEtaDay(order.estimatedDeliveryUtc),
      }
    : null;
  // audit-3 A2: courier orders must show the delivery address — before this
  // the address rode ProviderOrderDto.ShippingAddress but was rendered
  // nowhere provider-side. c3e7f6d0: DropoffPoint rows are EXCLUDED now —
  // their ShippingAddress is the carrier point address, which must never
  // render (service name only); legacy rows keep the address.
  const courierAddress = addressStrip?.kind === 'address' ? addressStrip.address : null;
  const carrierService = addressStrip?.kind === 'service' ? addressStrip.serviceName : null;
  return {
    ...buildProviderOrderBase(order),
    delivery: { methodLabel: delivery.methodLabel, text: delivery.text },
    pickupPoint,
    courierAddress,
    carrierService,
    addressStrip,
    eta,
  };
}

/* da2f1e1b: ProviderOrder → timeline input, so the queue strip shares the
 * customer timeline's dated milestones instead of an undated skeleton —
 * result submission (earliest Result photo), customer confirmation,
 * completion, and the dispute stamp all render with real timestamps when
 * the API has them. Legacy rows without the new fields degrade exactly as
 * before (null stamps, same pickup/courier copy rules). Pure on purpose:
 * runs under `node --test` like the rest of this module. */
export function providerTimelineInput(order: ProviderOrder): OrderTimelineInput {
  return {
    status: order.status,
    createdAtUtc: order.createdAtUtc,
    customerConfirmedAtUtc: order.customerConfirmedAtUtc,
    completedAtUtc: order.completedAtUtc ?? null,
    disputedAtUtc: order.disputedAtUtc ?? null,
    resultSubmittedAtUtc: order.resultSubmittedAtUtc ?? null,
    deliveryMethod: order.deliveryMethod ?? null,
  };
}
